
import React, { useState, useEffect } from "react";
import { useToast } from "../components/Toast";
import { useAuth } from "../context/AuthContext";
import { isSuperUser } from "../config/roles";
import { analyzeUploadedFile } from "../services/aiService";
import { uploadTempFile } from "../services/storageUpload";
import { functions, db } from "../../firebaseClient";
// Import new tab
import { DashboardTodosLiveDebugTab } from "../components/debug/DashboardTodosLiveDebugTab";
import { DashboardTodosDebugTab } from "../components/debug/DashboardTodosDebugTab"; // Reuse existing logic checker
import type { NavKey } from "../Sidebar";

// Define Props for Navigation (Optional now, but kept for signature)
interface Props {
    onNavigate?: (key: NavKey) => void;
}

const SystemDebugPage: React.FC<Props> = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'LIVE_DEBUG' | 'TODOS_DATA' | 'QUEUE' | 'SEAL_CHECK' | 'AI_TEST' | 'EMAIL_DEBUG'>('LIVE_DEBUG');
  
  if (!isSuperUser(user?.email)) {
      return <div className="p" style={{textAlign:'center', padding:40, color:'var(--danger)'}}>403 Forbidden</div>;
  }

  return (
    <div className="card" style={{ width: '100%', maxWidth: '100%', height: '85vh', display: 'flex', flexDirection: 'column', background: '#161616', border: '1px solid #333', boxSizing: 'border-box' }}>
      <div style={{ borderBottom: "1px solid #333", padding: "16px 20px", display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 className="h1" style={{ margin: 0, fontSize: 18, fontWeight: 'bold' }}>
            系統檢測與工具 (Debug)
          </h1>
          {/* Removed "Go to Dashboard" button as requested */}
      </div>
      
      <div className="row" style={{ borderBottom: "1px solid #333", padding: "0 12px", overflowX: 'auto', flexShrink: 0 }}>
        <TabButton label="今日待辦摘要 Live 連線偵錯" active={activeTab === 'LIVE_DEBUG'} onClick={() => setActiveTab('LIVE_DEBUG')} />
        <TabButton label="待辦數據與一致性 (Data)" active={activeTab === 'TODOS_DATA'} onClick={() => setActiveTab('TODOS_DATA')} />
        <TabButton label="郵件佇列 (Queue)" active={activeTab === 'QUEUE'} onClick={() => setActiveTab('QUEUE')} />
        <TabButton label="SEAL 落點檢測" active={activeTab === 'SEAL_CHECK'} onClick={() => setActiveTab('SEAL_CHECK')} />
        <TabButton label="AI 分析測試" active={activeTab === 'AI_TEST'} onClick={() => setActiveTab('AI_TEST')} />
        <TabButton label="Email 發送測試" active={activeTab === 'EMAIL_DEBUG'} onClick={() => setActiveTab('EMAIL_DEBUG')} />
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {activeTab === 'LIVE_DEBUG' && <DashboardTodosLiveDebugTab />}
        {activeTab === 'TODOS_DATA' && <DashboardTodosDebugTab />}
        {activeTab === 'QUEUE' && <MailQueueMonitor />}
        {activeTab === 'SEAL_CHECK' && <SealLocationCheck />}
        {activeTab === 'AI_TEST' && <AiSmokeTest />}
        {activeTab === 'EMAIL_DEBUG' && <EmailDebugSection />}
      </div>
    </div>
  );
};

const TabButton = ({ label, active, onClick }: any) => (
  <button 
    className="navbtn" 
    style={{ 
      width: 'auto', 
      borderBottom: active ? '2px solid #3b82f6' : '2px solid transparent', 
      borderRadius: 0, 
      color: active ? '#fff' : '#888',
      fontWeight: active ? 600 : 400,
      whiteSpace: 'nowrap'
    }}
    onClick={onClick}
  >
    {label}
  </button>
);

// --- Email Debug Section ---
function EmailDebugSection() {
    const { showToast } = useToast();
    const [to, setTo] = useState("kevinckc1983@gmail.com");
    const [subject, setSubject] = useState("[LegalOps] Debug Email Test");
    const [body, setBody] = useState("This is a test email sent from System Debug Page.");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<any>(null);

    const handleSend = async () => {
        if (!to) return alert("Missing recipient");
        setLoading(true);
        setResult(null);
        
        try {
            const sendFn = functions.httpsCallable('sendTestEmailDebug');
            const res = await sendFn({ to, subject, text: body });
            setResult(res.data);
            
            if ((res.data as any).ok) {
                showToast("寄送成功", "success");
            } else {
                showToast("寄送回報失敗", "error");
            }
        } catch (e: any) {
            console.error(e);
            setResult({ ok: false, error: e.message, details: e });
            showToast("呼叫失敗", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <h3 className="h1" style={{ margin: 0 }}>發送測試</h3>
                
                <div className="field">
                    <label className="label">收件人 (To)</label>
                    <input className="input" value={to} onChange={e => setTo(e.target.value)} />
                </div>
                
                <div className="field">
                    <label className="label">主旨 (Subject)</label>
                    <input className="input" value={subject} onChange={e => setSubject(e.target.value)} />
                </div>
                
                <div className="field">
                    <label className="label">內文 (Body Text)</label>
                    <textarea className="input" rows={4} value={body} onChange={e => setBody(e.target.value)} />
                </div>

                <div className="row" style={{ justifyContent: 'flex-end' }}>
                    <button className="btn" style={{ width: 'auto' }} onClick={handleSend} disabled={loading}>
                        {loading ? "發送中..." : "Send Test Email"}
                    </button>
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <h3 className="h1" style={{ margin: 0 }}>Result / Log</h3>
                <div style={{ 
                    flex: 1, 
                    background: '#000', 
                    border: '1px solid #333', 
                    borderRadius: 8, 
                    padding: 12, 
                    fontFamily: 'monospace', 
                    fontSize: 12, 
                    color: result?.ok ? '#4ade80' : result?.ok === false ? '#f87171' : '#aaa',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflowX: 'auto',
                    maxHeight: '400px',
                    overflowY: 'auto'
                }}>
                    {result ? JSON.stringify(result, null, 2) : "等待發送..."}
                </div>
            </div>
        </div>
    );
}

// --- AI Smoke Test Component ---
function AiSmokeTest() {
    const { showToast } = useToast();
    const [prompt, setPrompt] = useState("");
    const [contractId, setContractId] = useState("");
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [resultLog, setResultLog] = useState<string>("");

    const log = (msg: string, data?: any) => {
        const ts = new Date().toLocaleTimeString();
        const str = data ? `${msg}\n${JSON.stringify(data, null, 2)}` : msg;
        setResultLog(prev => `[${ts}] ${str}\n\n${prev}`);
        console.log(`[AI_TEST] ${msg}`, data || "");
    };

    const runAnalysis = async (mode: 'ATTACHMENT' | 'CONTRACT') => {
        if (!file && !contractId && !prompt) {
            log("❌ Error: 請至少提供 檔案, ContractID 或 Prompt");
            return;
        }

        setLoading(true);
        log(`🚀 Starting ${mode} Analysis...`);

        try {
            let uploadedMeta = undefined;

            if (file) {
                log(`Uploading file: ${file.name}...`);
                uploadedMeta = await uploadTempFile(file);
                log("File Uploaded:", uploadedMeta);
            }

            const payload: any = {
                userInstruction: prompt,
                contractId: contractId || undefined,
                files: uploadedMeta ? [uploadedMeta] : undefined,
                purpose: mode === 'CONTRACT' ? 'contract_review' : 'general_analysis',
                mode: mode === 'CONTRACT' ? 'analysis' : 'summary'
            };

            log("Sending Payload to analyzeUploadedFile:", payload);

            const result = await analyzeUploadedFile(payload);
            
            log("✅ Success Response:", result);
            showToast("測試成功");

        } catch (e: any) {
            log("❌ FAILED:", { message: e.message, details: e });
            showToast("測試失敗: " + e.message, "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{display:'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20}}>
            <div>
                <h3 className="h1" style={{marginBottom:16}}>輸入參數</h3>
                
                <div className="field">
                    <label className="label">Contract ID (Optional)</label>
                    <input className="input" value={contractId} onChange={e => setContractId(e.target.value)} placeholder="e.g. 既有合約 ID" />
                    <div style={{fontSize:11, color:'#666', marginTop:4}}>若不選檔案，將以此 ID 去資料庫找附件</div>
                </div>

                <div className="field">
                    <label className="label">Upload File (Optional)</label>
                    <input type="file" onChange={e => setFile(e.target.files?.[0] || null)} style={{color:'#fff'}} />
                    {file && <div style={{fontSize:12, color:'#fbbf24', marginTop:4}}>已選: {file.name} ({(file.size/1024).toFixed(1)} KB)</div>}
                </div>

                <div className="field">
                    <label className="label">Prompt (Optional)</label>
                    <textarea className="input" rows={3} value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="額外指令..." />
                </div>

                <div className="row" style={{marginTop:20, gap:10, flexWrap:'wrap'}}>
                    <button className="btn" onClick={() => runAnalysis('ATTACHMENT')} disabled={loading}>
                        {loading ? "Running..." : "Run: 附件AI解析 (General)"}
                    </button>
                    <button className="btn" style={{background:'#8b5cf6', border:'none', color:'#fff'}} onClick={() => runAnalysis('CONTRACT')} disabled={loading}>
                        {loading ? "Running..." : "Run: 合約AI摘要 (Contract)"}
                    </button>
                </div>
            </div>

            <div style={{display:'flex', flexDirection:'column', height:'400px'}}>
                <div style={{display:'flex', justifyContent:'space-between', marginBottom:8}}>
                    <h3 className="h1" style={{margin:0}}>執行紀錄 (Logs)</h3>
                    <button className="smallbtn" onClick={() => setResultLog("")}>Clear</button>
                </div>
                <div style={{
                    flex:1, 
                    background:'#000', 
                    border:'1px solid #333', 
                    borderRadius:8, 
                    padding:12, 
                    fontFamily:'monospace', 
                    fontSize:12, 
                    whiteSpace:'pre-wrap', 
                    wordBreak: 'break-word',
                    overflowY:'auto',
                    color:'#0f0'
                }}>
                    {resultLog || "Ready..."}
                </div>
            </div>
        </div>
    );
}

// Reuse Queue and Seal components
function MailQueueMonitor() {
    const [queueItems, setQueueItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const { showToast } = useToast();

    const refresh = async () => {
        setLoading(true);
        try {
            const q = db.collection("mail_queue").orderBy("createdAt", "desc").limit(30);
            const snap = await q.get();
            setQueueItems(snap.docs.map(d => ({id: d.id, ...d.data()})));
        } catch(e) { console.error(e); }
        finally { setLoading(false); }
    };

    const handleRetry = async (id: string) => {
        if (!confirm("確定標記重試？(需等待後端排程拾取)")) return;
        try {
            await db.collection("mail_queue").doc(id).update({
                status: 'PENDING',
                attempts: 0,
                lastError: null,
                retryRequested: true
            });
            showToast("已標記重試");
            await refresh();
        } catch (e: any) {
            showToast("操作失敗: " + e.message, "error");
        }
    };

    useEffect(() => { refresh(); }, []);

    const getStatusBadge = (s: string) => {
        const color = s === 'SENT' ? 'var(--ok)' : s === 'ERROR' ? 'var(--danger)' : s === 'SENDING' ? '#fbbf24' : '#aaa';
        return <span className="badge" style={{color, borderColor: color}}>{s}</span>
    };

    return (
        <div>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12}}>
                <div style={{fontWeight:'bold'}}>Mail Queue (Latest 30)</div>
                <button className="smallbtn" onClick={refresh} disabled={loading}>🔄 重新整理</button>
            </div>
            <div style={{border:'1px solid #333', borderRadius:8, overflowX:'auto'}}>
                <table style={{width:'100%', borderCollapse:'collapse', fontSize:12, minWidth: 600}}>
                    <thead style={{background:'#222', color:'#aaa', textAlign:'left'}}>
                        <tr>
                            <th style={{padding:8}}>時間</th>
                            <th style={{padding:8}}>收件人</th>
                            <th style={{padding:8}}>主旨</th>
                            <th style={{padding:8}}>狀態</th>
                            <th style={{padding:8}}>錯誤訊息</th>
                            <th style={{padding:8}}>操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        {queueItems.map(item => (
                            <tr key={item.id} style={{borderBottom:'1px solid #333'}}>
                                <td style={{padding:8}}>{item.createdAt?.toDate ? item.createdAt.toDate().toLocaleString() : '-'}</td>
                                <td style={{padding:8}}>{Array.isArray(item.to) ? item.to.join(', ') : item.to}</td>
                                <td style={{padding:8}}>{item.subject}</td>
                                <td style={{padding:8}}>{getStatusBadge(item.status)}</td>
                                <td style={{padding:8, color:'var(--danger)'}}>
                                    {item.lastError || (item.status==='PENDING' ? '等待處理器...' : '-')}
                                </td>
                                <td style={{padding:8}}>
                                    {(item.status === 'ERROR' || item.status === 'PENDING') && (
                                        <button className="smallbtn" onClick={() => handleRetry(item.id)}>重試</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function SealLocationCheck() {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<any>(null);

    const scan = async () => {
        setLoading(true);
        setResult(null);
        try {
            // 1. Check seal_requests (Legacy)
            const snap1 = await db.collection("seal_requests").orderBy("createdAt", "desc").limit(3).get();
            const count1 = (await db.collection("seal_requests").get()).size; 

            // 2. Check approval_requests
            const q2a = db.collection("approval_requests").where("module", "==", "SEAL_REQUEST");
            const snap2a = await q2a.limit(3).get();
            const count2a = (await q2a.get()).size;

            const q2b = db.collection("approval_requests").where("module", "==", "sealRequest");
            const snap2b = await q2b.limit(3).get();
            const count2b = (await q2b.get()).size;

            setResult({
                sealRequests: {
                    count: count1,
                    samples: snap1.docs.map(d => `${d.id} / ${d.data().status} / ${d.data().title}`)
                },
                approvalRequestsUpper: {
                    count: count2a,
                    samples: snap2a.docs.map(d => `${d.id} / ${d.data().status} / ${d.data().title}`)
                },
                approvalRequestsLower: {
                    count: count2b,
                    samples: snap2b.docs.map(d => `${d.id} / ${d.data().status} / ${d.data().title}`)
                }
            });

        } catch(e: any) {
            console.error(e);
            alert("Scan failed: " + e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:16}}>
                <h3 style={{fontSize:16, fontWeight:'bold', margin:0}}>SEAL 落點檢測</h3>
                <button className="smallbtn" onClick={scan} disabled={loading}>{loading ? "掃描中..." : "開始掃描"}</button>
            </div>
            
            <div style={{background: '#111', padding: 20, borderRadius: 8, fontSize: 13, fontFamily: 'monospace', color: '#ccc'}}>
                {!result && "點擊掃描以查看用印資料分佈..."}
                {result && (
                    <div style={{display:'flex', flexDirection:'column', gap: 20}}>
                        <div>
                            <div style={{color:'#fbbf24', fontWeight:'bold', marginBottom:4}}>Collection: seal_requests (Legacy)</div>
                            <div>Total: {result.sealRequests.count}</div>
                            {result.sealRequests.samples.map((s: string, i: number) => <div key={i}>- {s}</div>)}
                        </div>
                        <div>
                            <div style={{color:'#60a5fa', fontWeight:'bold', marginBottom:4}}>Collection: approval_requests (module="SEAL_REQUEST")</div>
                            <div>Total: {result.approvalRequestsUpper.count}</div>
                            {result.approvalRequestsUpper.samples.map((s: string, i: number) => <div key={i}>- {s}</div>)}
                        </div>
                        <div>
                            <div style={{color:'#a78bfa', fontWeight:'bold', marginBottom:4}}>Collection: approval_requests (module="sealRequest")</div>
                            <div>Total: {result.approvalRequestsLower.count}</div>
                            {result.approvalRequestsLower.samples.map((s: string, i: number) => <div key={i}>- {s}</div>)}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// Re-using TodosMonitor logic as "Data Consistency" tab
function TodosMonitor() {
    const [todos, setTodos] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    
    const refresh = async () => {
        setLoading(true);
        try {
            const q = db.collection("todos").orderBy("updatedAt", "desc").limit(50);
            const snap = await q.get();
            setTodos(snap.docs.map(d => ({id: d.id, ...d.data()})));
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };
    
    useEffect(() => { refresh(); }, []);

    return (
        <div>
             <div style={{marginBottom:16, display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                <h3>待辦數據檢視 (Snapshot)</h3>
                <button className="smallbtn" onClick={refresh} disabled={loading}>Refresh</button>
             </div>
             <div style={{border:'1px solid #333', borderRadius:8, overflowX:'auto'}}>
                <table style={{width:'100%', borderCollapse:'collapse', fontSize:12}}>
                    <thead style={{background:'#222'}}>
                        <tr>
                            <th style={{padding:8}}>ID</th>
                            <th style={{padding:8}}>Title</th>
                            <th style={{padding:8}}>Todo Status</th>
                            <th style={{padding:8}}>Updated</th>
                        </tr>
                    </thead>
                    <tbody>
                        {todos.map(t => (
                            <tr key={t.id} style={{borderBottom:'1px solid #333'}}>
                                <td style={{padding:8}}>{t.id}</td>
                                <td style={{padding:8}}>{t.title}</td>
                                <td style={{padding:8, color: t.todoStatus==='OPEN'?'var(--ok)':'#666'}}>{t.todoStatus}</td>
                                <td style={{padding:8}}>{t.updatedAt?.toDate?.().toLocaleString()}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
             </div>
        </div>
    );
}

export default SystemDebugPage;
